import { useEffect, useState } from 'react';
import { CircleAlert, KeyRound, Pencil, RefreshCw, Search } from 'lucide-react';
import type { Connection } from '../../../types/electron';
import { Button } from '../../../components/ui/button';
import { getConnectionErrorPresentation } from '../utils/connectionErrorPresentation';
import { redactTargetSecrets } from '../../../types/connectionTransfer';
import {
  getConnectionErrorFix,
  isApplyConnectionErrorFix,
  isNavigateConnectionErrorFix,
  type ConnectionErrorActionFixKind,
  type ConnectionErrorFixRetry,
} from '../utils/connectionErrorFix';
import { OracleExpiredPasswordDialog } from './OracleExpiredPasswordDialog';
import { OracleListenerServicesBrowser } from './OracleListenerServicesBrowser';
import { classifyDbError } from '../../../core/analytics/dbErrorClassifier';
import { useAnalytics } from '../../../core/analytics/useAnalytics';

type ConnectionErrorNoticeProps = {
  error: string;
  /**
   * The failing connection. The full record (not just the TLS fields) because
   * the Oracle fixes need the connect target and credentials: the expired-
   * password dialog performs the change over IPC, and the listener browser
   * probes server:port.
   */
  connection?: Connection;
  /** Structured driver code when it survived IPC — sharpens classification. */
  errorCode?: string;
  /** Which UI shows this notice (analytics property — low cardinality). */
  surface: 'connection-dialog' | 'connections-list';
  /**
   * When provided and the error maps to a one-click fix (e.g. an untrusted
   * server certificate), renders a chip that flips the setting and retries.
   * The caller owns the flip + retry so the setting is kept only on success.
   */
  onApplyFix?: (fix: ConnectionErrorFixRetry) => void;
  /**
   * When provided and the error maps to an editor-route fix — a credential
   * failure ("Update user & password…") or an unreachable/missing target
   * ("Edit connection…") — renders a chip that opens the connection editor
   * (neither has a retryable field flip — the fix IS editing).
   */
  onEditConnection?: () => void;
  /** Disables the fix chip while the retry it triggered is in flight. */
  fixBusy?: boolean;
  /** `ssh`: the SSH tunnel failed (the dialog's result strip) — the notice says so in its title. */
  origin?: 'ssh';
  /** Overrides the title (an SSH refusal names itself: "SSH host key changed" is a security warning, not an outage). */
  title?: string;
};

/**
 * Messages quote commands, paths and hosts in backticks (the SSH tunnel's errors: `ssh-add -l`, `~/.ssh/...`); they are
 * shown as code, never as literal backticks (Option C C2 copy defect).
 */
export function InlineCodeText({ text }: { text: string }) {
  const parts = text.split(/`([^`\n]+)`/u);
  return (
    <>
      {parts.map((part, index) => (index % 2 === 1
        ? <code key={index} className="rounded bg-muted px-1 py-px font-mono text-[0.85em]">{part}</code>
        : part))}
    </>
  );
}

const FIX_CHIP_ICONS = {
  change_expired_password: KeyRound,
  browse_listener_services: Search,
  edit_credentials: Pencil,
  edit_connection: Pencil,
} as const;

export function ConnectionErrorNotice({
  error,
  connection,
  errorCode,
  surface,
  onApplyFix,
  onEditConnection,
  fixBusy,
  origin,
  title,
}: ConnectionErrorNoticeProps) {
  const { track } = useAnalytics();
  // A driver's error can echo a connect string or a descriptor: secrets inside it are masked (C2 review C2-03).
  const shown = getConnectionErrorPresentation(error, { ...connection, errorCode, origin });
  const presentation = { ...shown, message: redactTargetSecrets(shown.message), details: redactTargetSecrets(shown.details) };
  const classification = classifyDbError(connection?.engine, error, errorCode);
  const candidateFix =
    onApplyFix || onEditConnection
      ? getConnectionErrorFix(classification.category, connection?.engine, {
          trustServerCertificate: connection?.trustServerCertificate,
          code: classification.code,
          oracleConnectionStringType: connection?.oracleConnectionStringType,
          sshTunnelEnabled: connection?.sshTunnel?.enabled,
          canEditConnection: Boolean(onEditConnection),
          authentication: connection?.authentication,
        })
      : undefined;
  // Keep the hint's `fix` property honest: only report a kind whose chip can
  // actually render here (retry fixes need onApplyFix, the editor-route fixes
  // need onEditConnection).
  const fix =
    candidateFix &&
    (isNavigateConnectionErrorFix(candidateFix) ? onEditConnection : onApplyFix)
      ? candidateFix
      : undefined;

  // Which interactive Oracle fix flow is open (password dialog / listener
  // browser). Reset whenever the notice shows a different failure, so a
  // browser opened for one error never lingers under the next one.
  const [activeActionFix, setActiveActionFix] = useState<ConnectionErrorActionFixKind | null>(null);
  useEffect(() => {
    setActiveActionFix(null);
  }, [error, errorCode]);

  // Side-effect event: an actionable hint was shown for a classified failure.
  // All reported properties are deps, so a notice re-used for a different
  // failure never reports the previous failure's classification; `track` is
  // stable (useCallback([])) and the rest are strings, so a plain re-render
  // of the same failure still fires exactly once.
  const engine = connection?.engine ?? 'unknown';
  const { category, code } = classification;
  const fixKind = fix?.kind ?? 'none';
  useEffect(() => {
    track('feature:connection_error_hint', {
      engine,
      category,
      ...(code ? { code } : {}),
      fix: fixKind,
      surface,
    });
  }, [track, error, errorCode, engine, category, code, fixKind, surface]);

  const ChipIcon = fix && !isApplyConnectionErrorFix(fix) ? FIX_CHIP_ICONS[fix.kind] : RefreshCw;

  return (
    // Round B U2: the title (and its icon) carry the error colour; the explanation is normal foreground text.
    <div className="p-3 rounded-md border border-destructive/30 bg-destructive/10 text-sm text-foreground">
      <div className="flex items-center gap-1.5 font-medium text-destructive">
        <CircleAlert className="h-4 w-4 shrink-0" aria-hidden />
        {title ?? presentation.title}
      </div>
      <div className="mt-1 break-words" data-testid="connection-error-message">
        <InlineCodeText text={presentation.message} />
      </div>

      {fix && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={fixBusy}
          data-testid="connection-error-fix-button"
          className="mt-2 h-7 gap-1.5 border-destructive/40 bg-transparent text-destructive hover:bg-destructive/15 hover:text-destructive"
          onClick={() => {
            track('feature:connection_fix_clicked', {
              engine: connection?.engine ?? 'unknown',
              category: classification.category,
              fix: fix.kind,
              surface,
            });
            if (isNavigateConnectionErrorFix(fix)) {
              onEditConnection?.();
            } else if (isApplyConnectionErrorFix(fix)) {
              onApplyFix?.({ kind: fix.kind, apply: fix.apply });
            } else {
              setActiveActionFix(fix.kind);
            }
          }}
        >
          <ChipIcon className="h-3.5 w-3.5" />
          {fix.label}
        </Button>
      )}

      {activeActionFix === 'browse_listener_services' && connection && onApplyFix && (
        <OracleListenerServicesBrowser
          server={connection.server}
          port={connection.port}
          hint={connection.oracleServiceName || connection.database}
          onPick={(service) => {
            setActiveActionFix(null);
            onApplyFix({
              kind: 'browse_listener_services',
              // A picked value is always a SERVICE name (that's what the
              // probes dial), so an SID-mode connection switches modes.
              apply: { oracleServiceName: service, oracleUseSID: false },
            });
          }}
        />
      )}

      {activeActionFix === 'change_expired_password' && connection && onApplyFix && (
        <OracleExpiredPasswordDialog
          connection={connection}
          onClose={() => setActiveActionFix(null)}
          onPasswordChanged={(newPassword) => {
            setActiveActionFix(null);
            onApplyFix({
              kind: 'change_expired_password',
              apply: { password: newPassword },
              // The server-side password has already changed — reverting the
              // field on a failed retry would leave a value that can never
              // connect again.
              keepOnFailure: true,
            });
          }}
        />
      )}

      {/* Nothing more to disclose when the message IS the error (an SSH tunnel sentence). */}
      {presentation.details !== presentation.message && (
        <details className="mt-2">
          <summary className="cursor-pointer select-none text-xs text-muted-foreground hover:text-foreground">
            Error details
          </summary>
          <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-destructive/5 p-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
            {presentation.details}
          </pre>
        </details>
      )}
    </div>
  );
}
